import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/integrations/supabase/client'
import { useOrgPermissions } from '@/hooks/useOrgPermissions'
import { useToast } from '@/hooks/use-toast'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog'
import { Badge } from '@/components/ui/badge'
import { Network, Plus, Trash2, ChevronRight, ChevronDown, Loader2 } from 'lucide-react'
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog'

interface OrgUnit {
  id: string
  name: string
  type: string
  color: string | null
  display_order: number
  parent_unit_id: string | null
  org_id: string
  children?: OrgUnit[]
}

const UNIT_TYPE_LABELS: Record<string, string> = {
  department:  'Department',
  division:    'Division',
  unit:        'Unit',
  team:        'Team',
  office:      'Office',
}

const TYPE_COLORS: Record<string, string> = {
  department: 'bg-blue-100 text-blue-800',
  division:   'bg-purple-100 text-purple-800',
  unit:       'bg-emerald-100 text-emerald-800',
  team:       'bg-amber-100 text-amber-800',
  office:     'bg-rose-100 text-rose-800',
}

function buildTree(units: OrgUnit[]): OrgUnit[] {
  const map = new Map<string, OrgUnit>(units.map(u => [u.id, { ...u, children: [] }]))
  const roots: OrgUnit[] = []
  for (const unit of map.values()) {
    if (!unit.parent_unit_id) {
      roots.push(unit)
    } else {
      const parent = map.get(unit.parent_unit_id)
      if (parent) {
        parent.children = parent.children ?? []
        parent.children.push(unit)
      } else {
        roots.push(unit)
      }
    }
  }
  const sortChildren = (nodes: OrgUnit[]) => {
    nodes.sort((a, b) => a.display_order - b.display_order)
    nodes.forEach(n => n.children && sortChildren(n.children))
  }
  sortChildren(roots)
  return roots
}

function UnitNode({
  unit,
  depth,
  allUnits,
  canEdit,
  onDelete,
}: {
  unit: OrgUnit
  depth: number
  allUnits: OrgUnit[]
  canEdit: boolean
  onDelete: (id: string, name: string) => void
}) {
  const [expanded, setExpanded] = useState(true)
  const hasChildren = (unit.children?.length ?? 0) > 0
  const typeLabel = UNIT_TYPE_LABELS[unit.type] ?? unit.type
  const typeBadge = TYPE_COLORS[unit.type] ?? 'bg-zinc-100 text-zinc-700'

  return (
    <div className={depth > 0 ? 'ml-6 border-l border-border pl-4 mt-2' : ''}>
      <div className="flex items-center gap-2 group py-1">
        <button
          onClick={() => hasChildren && setExpanded(e => !e)}
          className={`w-5 h-5 shrink-0 flex items-center justify-center rounded text-muted-foreground hover:text-foreground transition-colors ${!hasChildren ? 'invisible' : ''}`}
        >
          {expanded ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
        </button>

        <div
          className="w-3 h-3 rounded-full shrink-0"
          style={{ backgroundColor: unit.color ?? '#94a3b8' }}
        />

        <span className="text-sm font-medium flex-1 leading-tight">{unit.name}</span>

        <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${typeBadge}`}>
          {typeLabel}
        </span>

        {canEdit && (
          <button
            onClick={() => onDelete(unit.id, unit.name)}
            className="opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-destructive"
            title="Delete unit"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {expanded && hasChildren && (
        <div>
          {unit.children!.map(child => (
            <UnitNode
              key={child.id}
              unit={child}
              depth={depth + 1}
              allUnits={allUnits}
              canEdit={canEdit}
              onDelete={onDelete}
            />
          ))}
        </div>
      )}
    </div>
  )
}

export function OrgUnitTree() {
  const { orgId, can } = useOrgPermissions()
  const { toast } = useToast()
  const qc = useQueryClient()
  const canEdit = can('organogram', 'edit')

  const [addOpen, setAddOpen] = useState(false)
  const [form, setForm] = useState({ name: '', type: 'department', color: '#0D2240', parent_unit_id: '' })
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null)

  const { data: units = [], isLoading } = useQuery({
    queryKey: ['org-units', orgId],
    enabled: !!orgId,
    queryFn: async () => {
      const { data, error } = await (supabase
        .from('org_units' as any)
        .select('id, name, type, color, display_order, parent_unit_id, org_id')
        .eq('org_id', orgId!)
        .order('display_order') as any)
      if (error) throw error
      return (data ?? []) as OrgUnit[]
    },
  })

  const addMutation = useMutation({
    mutationFn: async (values: typeof form) => {
      const maxOrder = units.reduce((m, u) => Math.max(m, u.display_order), 0)
      const { error } = await (supabase.from('org_units' as any).insert({
        org_id: orgId!,
        name: values.name.trim(),
        type: values.type,
        color: values.color,
        parent_unit_id: values.parent_unit_id || null,
        display_order: maxOrder + 1,
      }) as any)
      if (error) throw error
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['org-units', orgId] })
      setAddOpen(false)
      setForm({ name: '', type: 'department', color: '#0D2240', parent_unit_id: '' })
      toast({ title: 'Unit added' })
    },
    onError: (e: any) => toast({ title: 'Failed to add unit', description: e.message, variant: 'destructive' }),
  })

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await (supabase.from('org_units' as any).delete().eq('id', id) as any)
      if (error) throw error
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['org-units', orgId] })
      setDeleteTarget(null)
      toast({ title: 'Unit removed' })
    },
    onError: (e: any) => toast({ title: 'Failed to remove unit', description: e.message, variant: 'destructive' }),
  })

  const tree = buildTree(units)

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Network className="w-4 h-4" />
          Organisational Structure
        </CardTitle>
        {canEdit && (
          <Dialog open={addOpen} onOpenChange={setAddOpen}>
            <DialogTrigger asChild>
              <Button size="sm" variant="outline">
                <Plus className="w-4 h-4 mr-1" /> Add Unit
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Add Organisational Unit</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 pt-2">
                <div className="space-y-1.5">
                  <Label>Name</Label>
                  <Input
                    placeholder="e.g. Communications Department"
                    value={form.name}
                    onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>Type</Label>
                  <Select value={form.type} onValueChange={v => setForm(f => ({ ...f, type: v }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(UNIT_TYPE_LABELS).map(([v, l]) => (
                        <SelectItem key={v} value={v}>{l}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Parent Unit (optional)</Label>
                  <Select value={form.parent_unit_id} onValueChange={v => setForm(f => ({ ...f, parent_unit_id: v === '_none' ? '' : v }))}>
                    <SelectTrigger><SelectValue placeholder="None (root level)" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_none">None (root level)</SelectItem>
                      {units.map(u => (
                        <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Colour</Label>
                  <div className="flex items-center gap-3">
                    <input
                      type="color"
                      value={form.color}
                      onChange={e => setForm(f => ({ ...f, color: e.target.value }))}
                      className="h-9 w-14 rounded border border-border cursor-pointer"
                    />
                    <span className="text-sm text-muted-foreground">{form.color}</span>
                  </div>
                </div>
                <div className="flex justify-end gap-2 pt-2">
                  <Button variant="outline" onClick={() => setAddOpen(false)}>Cancel</Button>
                  <Button
                    onClick={() => addMutation.mutate(form)}
                    disabled={!form.name.trim() || addMutation.isPending}
                  >
                    {addMutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                    Add Unit
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </CardHeader>

      <CardContent>
        {isLoading ? (
          <div className="flex items-center justify-center py-10">
            <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
          </div>
        ) : tree.length === 0 ? (
          <div className="py-8 text-center text-sm text-muted-foreground">
            No organisational units defined yet.
            {canEdit && ' Click "Add Unit" to build your org structure.'}
          </div>
        ) : (
          <div className="py-1">
            {tree.map(unit => (
              <UnitNode
                key={unit.id}
                unit={unit}
                depth={0}
                allUnits={units}
                canEdit={!!canEdit}
                onDelete={(id, name) => setDeleteTarget({ id, name })}
              />
            ))}
          </div>
        )}

        {units.length > 0 && (
          <div className="mt-4 pt-4 border-t border-border flex flex-wrap gap-2">
            {Object.entries(TYPE_COLORS).map(([type, cls]) => (
              <span key={type} className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${cls}`}>
                {UNIT_TYPE_LABELS[type]}
              </span>
            ))}
          </div>
        )}
      </CardContent>

      <AlertDialog open={!!deleteTarget} onOpenChange={open => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove "{deleteTarget?.name}"?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove the unit. Child units will become root-level units. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  )
}
